import { getDownloadURL, ref, uploadBytes, UploadResult } from 'firebase/storage'

import { Place, PlaceCategory, PlaceWithAnalytics, UpdatePlaceTDO } from '../types/utils'
import { LocationFilter, LocationQuery } from '../types/locations'
import placeQueries from './graphql/places/queries'
import placeMutations from './graphql/places/mutations'
import { storage } from '../utils/firebase'
import { graphQLClient } from './authService'
import { Experience } from '../types/experiences'
import experienceQueries from './graphql/experience/queries'

class PlaceService {
  getAll = async (variables: any = {}): Promise<Place[]> => {
    const response = await graphQLClient.request(placeQueries.getAll, variables)
    return response.places
  }

  getAllWithAnalytics = async (variables: any = undefined): Promise<PlaceWithAnalytics[]> => {
    if (variables === undefined) {
      const response = await graphQLClient.request(placeQueries.getAllWithAnalytics)
      return response.places
    }

    const response = await graphQLClient.request(placeQueries.getAllWithAnalytics, variables)
    return response.places
  }

  getAllWithFilter = async (
    variables: any = {},
    params: { [x: string]: string },
    filter?: LocationFilter
  ): Promise<Place[]> => {
    const where: LocationQuery = {
      OR: [{ name_contains: params.search }, { address_contains: params.search }],
    }

    if (filter) {
      if (filter.rating.length > 0) {
        where.averageEvaluation_in = filter.rating
      }

      if (filter.status.length > 0) {
        where.claimStatus_in = filter.status
      }
      if (filter.addresses.length > 0) {
        where.address_in = filter.addresses.map((place) => place.address)
      }

      if (filter.categories.length) {
        Object.assign(where, {
          categories_some: {
            id_in: filter.categories.map((category) => category.id),
          },
        })
      }

      if (filter.locations.length > 0) {
        Object.assign(where, {
          id_in: filter.locations.map((place) => place.id),
        })
      }

      // filter for customers
      if (filter.customers.length > 0) {
        Object.assign(where, {
          customer: {
            id_in: filter.customers.map((customer) => customer.id),
          },
        })
      }

      if (filter.nbExperiences.max < 1000 || filter.nbExperiences.min > 0) {
        where.nbExperiences_lte = filter.nbExperiences.max
        where.nbExperiences_gte = filter.nbExperiences.min
      }
    }

    return await this.getAll({
      ...variables,
      where,
    })
  }

  getCategories = async (inputString?: string): Promise<PlaceCategory[]> => {
    const response = await graphQLClient.request(placeQueries.getPlaceCategories, {
      where: { name_contains: inputString },
    })
    return response.placeCategories
  }

  getAllCategories = async (): Promise<PlaceCategory[]> => {
    const response = await graphQLClient.request(placeQueries.getPlaceCategories)
    return response.placeCategories
  }

  requestClaimPlace = async ({ googlePlaceId }: { googlePlaceId: string }): Promise<Place[]> => {
    const response = await graphQLClient.request(placeMutations.requestClaimPlace, { googlePlaceId })
    return response.requestClaimPlace
  }
  getById = async (id: string): Promise<Place> => {
    const response = await graphQLClient.request(placeQueries.getPlaceById, {
      where: {
        id,
      },
    })

    return response.place
  }

  claimPlaceById = async ({
    customerId,
    placeId,
  }: {
    customerId: string
    placeId: string
  }): Promise<{
    claimPlace: {
      id: string
    }
  }> => {
    return await graphQLClient.request(placeMutations.requestClaimPlaceByGoogleId, {
      customerId: customerId,
      where: {
        id: placeId,
      },
    })
  }

  claimPlaceByGoogleId = async ({
    customerId,
    googlePlaceId,
  }: {
    customerId: string
    googlePlaceId: string
  }): Promise<{
    claimPlace: {
      id: string
    }
  }> => {
    return await graphQLClient.request(placeMutations.requestClaimPlaceByGoogleId, {
      customerId: customerId,
      where: {
        googlePlaceId: googlePlaceId,
      },
    })
  }

  unclaimPlaceById = async ({
    placeId,
  }: {
    placeId: string
  }): Promise<{
    unclaimPlace: {
      id: string
    }
  }> => {
    return await graphQLClient.request(placeMutations.unclaimPlace, {
      where: {
        id: placeId,
      },
    })
  }

  uploadImage = async ({
    file,
    placeId,
    customerId,
  }: {
    file: File
    customerId: string
    placeId: string
  }): Promise<UploadResult> => {
    const timestamp = new Date().getTime()
    const fileRoute = `/customers/${customerId}/places/${placeId}/${timestamp}-${file.name}`
    return await uploadBytes(ref(storage, fileRoute), file)
  }

  updateById = async ({ id, data }: { id: string; data: UpdatePlaceTDO }): Promise<Place> => {
    let heroImage
    if (data.heroImage) {
      const imageUrl = await getDownloadURL(data.heroImage.ref)

      heroImage = {
        replace: {
          url: imageUrl,
          reference: data.heroImage.ref.fullPath,
        },
      }
    }

    const response = await graphQLClient.request(placeMutations.updatePlace, {
      where: {
        id: id,
      },
      data: {
        // Replace empty strings with null as expected by API
        description: data.description || null,
        actionText: data.actionText || null,
        actionUrl: data.actionUrl || null,
        heroImage: heroImage,
        categories: data.categories,
        configFlags: {
          update: data.configFlags,
        },
      },
    })

    return response.updatePlace
  }

  getExperiences = async (placeId: string, variables: any = {}): Promise<Experience[]> => {
    const response = await graphQLClient.request(experienceQueries.getAll, {
      ...variables,
      where: {
        place: { id: placeId },
      },
    })

    return response.experiences
  }

  count = async (where: any = {}) => {
    const response = await graphQLClient.request(placeQueries.countAll, {
      where,
    })

    return response?.placesConnection?.aggregate?.count || 0
  }

  respondToClaimRequest = async ({ placeId, action }: { placeId: string; action: 'APPROVE' | 'DENY' | 'CANCEL' }) => {
    const response = await graphQLClient.request(placeMutations.respondToPlaceClaimRequest, {
      placeId,
      action,
    })

    return response.respondToPlaceClaimRequest
  }
}

const placeService = new PlaceService()

export default placeService
